import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { firstRouteForWorkspace, workspaceOfPath } from '@/router/workspace-entry';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { title: '登录', public: true }
  },
  {
    path: '/workspaces',
    name: 'workspaces',
    component: () => import('@/views/WorkspaceChooserView.vue'),
    meta: { title: '选择后台', chooser: true }
  },
  {
    path: '/',
    component: () => import('@/layouts/AdminLayout.vue'),
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { title: '仪表盘', icon: 'Odometer', perm: 'dashboard:view' }
      },
      {
        path: 'user',
        name: 'user',
        component: () => import('@/views/user/UserListView.vue'),
        meta: { title: '用户中心', icon: 'User', perm: 'user:view' }
      },
      {
        path: 'user/:id',
        name: 'user-detail',
        component: () => import('@/views/user/UserDetailView.vue'),
        meta: { title: '用户详情', hidden: true, perm: 'user:view' }
      },
      {
        path: 'catalog',
        meta: { title: '设备目录', icon: 'Box' },
        redirect: '/catalog/types',
        children: [
          {
            path: 'types',
            name: 'catalog-types',
            component: () => import('@/views/catalog/DeviceTypeListView.vue'),
            meta: { title: '设备类型', icon: 'Box', perm: 'catalog:type:view' }
          },
          {
            path: 'models',
            name: 'catalog-models',
            component: () => import('@/views/catalog/ProductModelListView.vue'),
            meta: { title: '产品型号', icon: 'Cpu', perm: 'catalog:model:view' }
          },
          {
            path: 'detail-layouts',
            name: 'catalog-detail-layouts',
            component: () => import('@/views/catalog/ProductModelDetailLayoutListView.vue'),
            meta: { title: '详情页布局', icon: 'Grid', perm: 'catalog:detail:view' }
          },
          {
            path: 'shell-layouts',
            name: 'catalog-shell-layouts',
            component: () => import('@/views/catalog/ProductModelShellLayoutListView.vue'),
            meta: { title: '设置页布局', icon: 'Setting', perm: 'catalog:shell:view' }
          },
          {
            path: 'settings-fields',
            name: 'catalog-settings-fields',
            component: () => import('@/views/catalog/ProductModelSettingsFieldsListView.vue'),
            meta: { title: '设备设置字段', icon: 'EditPen', perm: 'catalog:settings:view' }
          },
          {
            path: 'event-notifications',
            name: 'catalog-event-notifications',
            component: () => import('@/views/catalog/ProductModelEventNotificationListView.vue'),
            meta: { title: '事件通知配置', icon: 'BellFilled', perm: 'catalog:notification:view' }
          },
          {
            path: 'alert-rules',
            name: 'catalog-alert-rules',
            component: () => import('@/views/catalog/ProductModelAlertRuleListView.vue'),
            meta: { title: '告警规则配置', icon: 'AlarmClock', perm: 'catalog:alert:view' }
          },
          {
            path: 'accessories',
            name: 'catalog-accessories',
            component: () => import('@/views/catalog/ProductModelAccessoryListView.vue'),
            meta: { title: '配件耗材', icon: 'Refresh', perm: 'catalog:model:view' }
          },
          {
            path: 'pairing-guides',
            name: 'catalog-pairing-guides',
            component: () => import('@/views/catalog/ProductModelPairingGuideListView.vue'),
            meta: { title: '配网指引', icon: 'Connection', perm: 'catalog:model:view' }
          },
          {
            path: 'video-resources',
            name: 'catalog-video-resources',
            component: () => import('@/views/catalog/VideoResourceListView.vue'),
            meta: { title: '资料资源', icon: 'VideoPlay', perm: 'catalog:notification:view' }
          },
          {
            path: 'firmware',
            name: 'catalog-firmware',
            component: () => import('@/views/catalog/FirmwareReleaseListView.vue'),
            meta: { title: '固件管理', icon: 'Upload', perm: 'device:firmware:view' }
          }
        ]
      },
      {
        path: 'device',
        name: 'device',
        component: () => import('@/views/device/DeviceListView.vue'),
        meta: { title: '设备运营', icon: 'Monitor', perm: 'device:view' }
      },
      {
        path: 'device/diagnose',
        name: 'device-diagnose',
        component: () => import('@/views/device/DeviceDiagnoseView.vue'),
        meta: { title: '设备诊断', icon: 'FirstAidKit', perm: 'device:diagnose' }
      },
      {
        path: 'device/:deviceId',
        name: 'device-detail',
        component: () => import('@/views/device/DeviceDetailView.vue'),
        meta: {
          title: '设备详情',
          hidden: true,
          anyPerm: ['device:view', 'device:diagnose']
        }
      },
      {
        path: 'event-catalog',
        name: 'event-catalog',
        component: () => import('@/views/eventcatalog/EventCatalogListView.vue'),
        meta: { title: '事件类型字典', icon: 'Bell', perm: 'event-catalog:view' }
      },
      {
        path: 'pet-recognition/training-readiness',
        name: 'pet-recognition-training-readiness',
        component: () => import('@/views/pet-recognition/TrainingReadinessView.vue'),
        meta: { title: '训练收益分析', icon: 'TrendCharts', perm: 'pet-recognition:analysis:view' }
      },
      {
        path: 'pet-recognition/events',
        name: 'pet-recognition-events',
        component: () => import('@/views/pet-recognition/RecognitionEventCenterView.vue'),
        meta: { title: '识别事件中心', icon: 'DataLine', perm: 'pet-recognition:events:view' }
      },
      {
        path: 'pet-recognition/registration-photos',
        name: 'pet-recognition-registration-photos',
        component: () => import('@/views/pet-recognition/RegistrationPoseView.vue'),
        meta: { title: '注册素材与姿态审核', icon: 'Picture', perm: 'pet-recognition:events:view' }
      },
      {
        path: 'community/sensitive-words',
        name: 'community-sensitive-words',
        component: () => import('@/views/community/SensitiveWordListView.vue'),
        meta: { title: '社区敏感词', icon: 'Warning', perm: 'community:word:view' }
      },
      {
        path: 'community/search-keywords',
        name: 'community-search-keywords',
        component: () => import('@/views/community/CommunitySearchKeywordListView.vue'),
        meta: { title: '搜索热词', icon: 'Search', perm: 'community:word:view' }
      },
      {
        path: 'community/posts',
        name: 'community-posts',
        component: () => import('@/views/community/CommunityPostListView.vue'),
        meta: { title: '社区帖子', icon: 'ChatDotRound', perm: 'community:post:view' }
      },
      {
        path: 'community/tags',
        name: 'community-tags',
        component: () => import('@/views/community/CommunityTagListView.vue'),
        meta: { title: '社区标签审核', icon: 'CollectionTag', perm: 'community:post:view' }
      },
      {
        path: 'community/reported-users',
        name: 'community-reported-users',
        component: () => import('@/views/community/CommunityReportedUserListView.vue'),
        meta: { title: '举报用户审核', icon: 'UserFilled', perm: 'community:post:view' }
      },
      {
        path: 'community/reported-groups',
        name: 'community-reported-groups',
        component: () => import('@/views/community/CommunityReportedGroupListView.vue'),
        meta: { title: '举报群聊审核', icon: 'ChatDotRound', perm: 'community:post:view' }
      },
      {
        path: 'mall',
        meta: { title: '官网商城', icon: 'Shop' },
        redirect: '/mall/products',
        children: [
          {
            path: 'products',
            name: 'mall-products',
            component: () => import('@/views/mall/MallProductListView.vue'),
            meta: { title: '商品管理', icon: 'Goods', perm: 'mall:product:view' }
          },
          {
            path: 'web-categories',
            name: 'mall-web-categories',
            component: () => import('@/views/mall/MallWebCategoryListView.vue'),
            meta: { title: '官网品类', icon: 'Grid', perm: 'mall:product:view' }
          },
          {
            path: 'orders',
            name: 'mall-orders',
            component: () => import('@/views/mall/MallOrderListView.vue'),
            meta: { title: '订单管理', icon: 'List', perm: 'mall:order:view' }
          },
          {
            path: 'after-sales',
            name: 'mall-after-sales',
            component: () => import('@/views/mall/MallAfterSaleListView.vue'),
            meta: { title: '售后管理', icon: 'RefreshLeft', perm: 'mall:after-sale:view' }
          },
          {
            path: 'exchanges',
            name: 'mall-exchanges',
            component: () => import('@/views/mall/MallExchangeListView.vue'),
            meta: { title: '换货管理', icon: 'Switch', perm: 'mall:after-sale:view' }
          },
          {
            path: 'site-media',
            name: 'mall-site-media',
            component: () => import('@/views/mall/MallSiteMediaView.vue'),
            meta: { title: '官网素材', icon: 'Picture', perm: 'mall:media:view' }
          },
          {
            path: 'faqs',
            name: 'mall-faqs',
            component: () => import('@/views/mall/MallFaqListView.vue'),
            meta: { title: '常见问题', icon: 'ChatDotRound', perm: 'mall:faq:view' }
          },
          {
            path: 'faq-categories',
            name: 'mall-faq-categories',
            component: () => import('@/views/mall/MallFaqCategoryListView.vue'),
            meta: { title: 'FAQ 分类', icon: 'CollectionTag', perm: 'mall:faq:view' }
          },
          {
            path: 'tutorials',
            name: 'mall-tutorials',
            component: () => import('@/views/mall/MallTutorialListView.vue'),
            meta: { title: '教学视频', icon: 'VideoPlay', perm: 'mall:tutorial:view' }
          }
        ]
      },
      {
        path: 'reward/redemption-items',
        name: 'reward-redemption-items',
        component: () => import('@/views/reward/RedemptionItemListView.vue'),
        meta: { title: '兑换商品', icon: 'Goods', perm: 'reward:item:view' }
      },
      {
        path: 'reward/reward-ops',
        name: 'reward-reward-ops',
        component: () => import('@/views/reward/RewardOpsListView.vue'),
        meta: { title: '奖励审核', icon: 'Medal', perm: 'reward:ops:view' }
      },
      {
        path: 'reward/recharge-skus',
        name: 'reward-recharge-skus',
        component: () => import('@/views/payment/RechargeSkuListView.vue'),
        meta: { title: '充值档位', icon: 'Wallet', perm: 'payment:sku:view' }
      },
      {
        path: 'activity/eligibility-config',
        name: 'activity-eligibility-config',
        component: () => import('@/views/activity/ActivityEligibilityConfigView.vue'),
        meta: { title: '发起资格配置', icon: 'Setting', perm: 'activity:config:view' }
      },
      {
        path: 'announcement/messages',
        name: 'announcement-messages',
        component: () => import('@/views/announcement/SystemMessageListView.vue'),
        meta: { title: '系统消息', icon: 'ChatDotSquare', perm: 'announcement:view' }
      },
      {
        path: 'feedback',
        name: 'feedback',
        component: () => import('@/views/feedback/UserFeedbackListView.vue'),
        meta: { title: '投诉建议', icon: 'ChatLineSquare', perm: 'feedback:view' }
      },
      // 三个分组目录本身没有页面（菜单表里 path 已置空，见 V310）。这里保留 redirect
      // 只为兜底：有人手输或收藏了 /support-center 这类地址时，落到组内第一项而不是 404。
      {
        path: 'support-center',
        redirect: '/support',
        meta: { title: '客服中心', hidden: true }
      },
      {
        path: 'ops-center',
        redirect: '/announcement/messages',
        meta: { title: '运营中心', hidden: true }
      },
      {
        path: 'system-center',
        redirect: '/app-version',
        meta: { title: '系统管理', hidden: true }
      },
      {
        path: 'support',
        name: 'support',
        component: () => import('@/views/support/SupportWorkbenchView.vue'),
        meta: { title: '会话工作台', icon: 'Service', perm: 'support:view' }
      },
      {
        path: 'support/agents',
        name: 'support-agents',
        component: () => import('@/views/support/SupportAgentListView.vue'),
        meta: { title: '坐席管理', icon: 'Avatar', perm: 'support:agent:view' }
      },
      {
        path: 'support/quick-replies',
        name: 'support-quick-replies',
        component: () => import('@/views/support/SupportQuickReplyListView.vue'),
        meta: { title: '快捷短语', icon: 'ChatLineSquare', perm: 'support:quick:view' }
      },
      {
        path: 'audit',
        name: 'audit',
        component: () => import('@/views/audit/AuditListView.vue'),
        meta: { title: '用户操作记录', icon: 'Document', perm: 'audit:view' }
      },
      {
        // 已决定不做（2026-06-05 产品决策）：C 端首页卡片远程配置暂不开发。
        // 路由/骨架页/admin.home_card_config 表先保留不删，待后续统一清理。
        path: 'content',
        name: 'content',
        component: () => import('@/views/content/ContentListView.vue'),
        meta: { title: '前端卡片配置', icon: 'Grid', perm: 'content:view' }
      },
      {
        path: 'app-version',
        name: 'app-version',
        component: () => import('@/views/system/AppVersionView.vue'),
        meta: { title: 'App 版本管理', icon: 'Iphone', perm: 'app_version:view' }
      },
      {
        path: 'system',
        name: 'system',
        component: () => import('@/views/system/AdminAccountView.vue'),
        meta: { title: '账号与权限', icon: 'Setting', perm: 'system:view' }
      }
    ]
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: '页面不存在', public: true }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

router.beforeEach(async (to, _from, next) => {
  const title = (to.meta?.title as string) ?? 'lulupaw 管理后台';
  document.title = `${title} - ${import.meta.env.VITE_APP_TITLE}`;

  const auth = useAuthStore();

  // 公开页（登录 / 404）：已登录访问登录页 → 先到工作区选择页
  if (to.meta?.public) {
    if (to.path === '/login' && auth.isAuthenticated) {
      return next('/workspaces');
    }
    return next();
  }

  // 需要登录
  if (!auth.isAuthenticated) {
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }

  // 刷新后补拉账号资料；token 失效则回登录
  try {
    await auth.ensureProfile();
  } catch {
    auth.setToken('');
    return next({ path: '/login', query: { redirect: to.fullPath } });
  }

  const avail = auth.availableWorkspaces;

  // 工作区选择页：已登录即可进；仅有一个可用工作区则免选直接进入
  if (to.meta?.chooser) {
    if (avail.length === 1) {
      auth.setWorkspace(avail[0]);
      return next(firstRouteForWorkspace(auth.allMenus, avail[0]));
    }
    return next();
  }

  // 无任何工作区权限 → 选择页（空态提示联系超管）
  if (avail.length === 0) {
    return next('/workspaces');
  }

  // 目标路由所属工作区：可访问则采用（深链 / 切换后侧栏跟随）
  const targetWs = workspaceOfPath(auth.allMenus, to.path);
  if (targetWs && avail.includes(targetWs)) {
    if (auth.workspace !== targetWs) auth.setWorkspace(targetWs);
  } else if (!auth.workspace || !avail.includes(auth.workspace)) {
    // 未选工作区（或所选已失效），且目标路由不归属可访问工作区 → 去选择页
    return next('/workspaces');
  }

  // 权限校验：越权回落到当前工作区首页
  const perm = to.meta?.perm as string | undefined;
  if (perm && !auth.hasPermission(perm)) {
    return next(firstRouteForWorkspace(auth.allMenus, auth.workspace || avail[0]));
  }
  const anyPerm = to.meta?.anyPerm as string[] | undefined;
  if (anyPerm?.length && !anyPerm.some((code) => auth.hasPermission(code))) {
    return next(firstRouteForWorkspace(auth.allMenus, auth.workspace || avail[0]));
  }

  next();
});

export default router;
